import * as React from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Button from '@mui/material/Button';
import { Avatar, Box, CircularProgress, Grid, IconButton, Snackbar, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { addUserEffects, approveAccountDeletionRequest, assignUserIdToDeletionRequest, cancelOffer, changeAdsStatusForUser, getAccountDeletionRequests, getBetlistsPaginated, getFullMatchInfo, getFunnelList, getMainProviderVarietyInfoDto, getOffers, getPromoBanners, getRacingResults, getTemplates, getUserAggregatedInfo, getUsersPaginated, getVirtualProviderMatchLiveInfo, rejectAccountDeletionRequest, resyncSingleMatch, setMatchBadState, updateBetStateForBetlist, updateUserBalance } from '../Api';
import { VIRTUAL_SPORT_IDS } from '../models/dtos/VirtualSportsCatalogDto';
import { liveActionLabel } from '../components/virtual-sports/VirtualMatchesTable';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PromoBannerDto from '../models/dtos/PromoBannerDto';
import PromoBannerCard from '../components/PromoBannerCard';
import TemplateDto from '../models/dtos/TemplateDto';
import { DataGrid } from '@mui/x-data-grid/DataGrid/DataGrid';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import FunnelDto from '../models/dtos/FunnelDto';
import FunnelConfigDto from '../models/dtos/FunnelConfigDto';
import { GridCellParams, GridValueGetterParams } from '@mui/x-data-grid/models/params/gridCellParams';
import { useCallback } from 'react';
import AccountDeletionRequestDto from '../models/dtos/AccountDeletionRequestDto';
import { rowSelectionStateInitializer } from '@mui/x-data-grid/internals';
import AccountMainInfoDto from '../models/dtos/AccountMainInfoDto';
import AggregateUserInfoDto from '../models/dtos/AggregateUserInfoDto';
import BetlistShortDto from '../models/dtos/BetlistShortDto';
import BetlistsTable from '../components/BetlistsTable';
import BalanceTable from '../components/BalanceTable';
import SupportChatTable from '../components/SupportChatTable';
import MatchInfoDto from '../models/dtos/MatchInfoDto';
import MatchVarietyDto from '../models/dtos/MatchVarietyDto';
import MatchAdditionalInfoDto from '../models/dtos/MatchAdditionalInfoDto';
import VarietiesTable from '../components/VarietiesTable';
import BetsForMatchesTable from '../components/BetsForMatchesTable';
import RacingParticipantResultDto from '../models/dtos/RacingParticipantResultDto';
import ProviderMatchEventInfoDto from '../models/dtos/ProviderMatchEventInfoDto';
import ProviderMatchEventInfoTables from '../components/ProviderMatchEventInfoTables';
import MatchBetsTab from '../components/match-page/MatchBetsTab';
import MatchHistoryTab from '../components/match-page/MatchHistoryTab';
import MatchParticipantsTab from '../components/match-page/MatchParticipantsTab';
import MatchPlayersTab from '../components/match-page/MatchPlayersTab';
import MatchStatisticsTab from '../components/match-page/MatchStatisticsTab';
import MatchVarietiesTab from '../components/match-page/MatchVarietiesTab';
import MatchExternalBetsTab from '../components/match-page/MatchExternalBetsTab';
import MatchBettingResultsTab from '../components/match-page/MatchBettingResultsTab';
import MatchAiPanel from '../components/match-briefs/MatchAiPanel';
import MatchPostsTab from '../components/publishing/MatchPostsTab';


export default function MatchPage() {
    let matchId = parseInt(useParams()['id'] ?? '0');

    const getTabIndexForQueryParams = (tab: string | null) => {
        if (tab != null) {
            switch (tab) {
                default: return 0;
                case 'bets': return 1;
                case 'external-bets': return 2;
                case 'betting-results': return 3;
                case 'statistics': return 4;
                case 'players': return 5;
                case 'history': return 6;
            }
        }

        return 0;
    }

    const [searchParams] = useSearchParams();

    const [state, setState] = React.useState({
        toastMsg: '' as string | null,
        tabIndex: getTabIndexForQueryParams(searchParams.get('tab')),
        loaded: false,
        matchId: matchId,
        matchInfo: {} as MatchInfoDto,
        providerMatchInfo: null as ProviderMatchEventInfoDto | null,
        providerInfoLoading: false,
        matchStats: { views: 0, bets: 0 } as MatchAdditionalInfoDto,
        varieties: [] as MatchVarietyDto[],
        racingParticipants: [] as RacingParticipantResultDto[]
    });

    const [providerSyncLoading, setProviderSyncLoading] = React.useState(false);
    const [badMatchLoading, setBadMatchLoading] = React.useState(false);
    const [copiedKey, setCopiedKey] = React.useState<string | null>(null);
    const [liveUrl, setLiveUrl] = React.useState<string | null>(null);
    const [liveUrlLoading, setLiveUrlLoading] = React.useState(false);

    const navigate = useNavigate();

    const handleCopy = React.useCallback(async (key: string, value?: string | number | null) => {
        if (value == null || value === '') {
            return;
        }

        try {
            await navigator.clipboard.writeText(value.toString());
            setCopiedKey(key);
            window.setTimeout(() => setCopiedKey(current => current === key ? null : current), 1500);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    }, []);

    const renderCopyableHeader = React.useCallback((label: string, value?: string | number | null) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{label}</span>
            <Tooltip title={copiedKey === label ? 'Copied!' : `Copy ${label}`}>
                <span>
                    <IconButton
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleCopy(label, value);
                        }}
                        sx={{
                            padding: '2px',
                            '&:hover': { backgroundColor: 'action.hover' }
                        }}
                    >
                        <ContentCopyIcon
                            fontSize="small"
                            sx={{ color: copiedKey === label ? 'success.main' : 'action.active' }}
                        />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    ), [copiedKey, handleCopy]);


    const getMatchStateText = (state: number) => {
        switch (state) {
            case 1: return 'Future';
            case 2: return 'Live';
            case 3: return 'Finished';
            default: return 'Processing';
            case 5: return 'Cancelled';
        }
    }


    const columns: GridColDef[] = [
        {
            field: 'id',
            headerName: 'id',
            width: 148,
            renderHeader: () => renderCopyableHeader('id', state.matchInfo?.id)
        },
        {
            field: 'sport.photo', headerName: 'Sport', width: 100, renderCell: (params: GridCellParams) =>
                <React.Fragment><Avatar
                    src={params.row.sport.photo_url}
                    sx={{ width: 40, height: 40 }}
                /></React.Fragment>
        },
        {
            field: 'sport.name', headerName: '', width: 150, renderCell: (params: GridCellParams) =>
                <React.Fragment>{params.row.sport?.name}</React.Fragment>
        },
        {
            field: 'league.name', headerName: 'League', width: 150, renderCell: (params: GridCellParams) =>
                <React.Fragment>{params.row.league?.name}</React.Fragment>
        },
        {
            field: 'startDate', headerName: 'Date', width: 250, renderCell: (params: GridCellParams) =>
                <React.Fragment>{new Date(params.row.date).toLocaleString()}</React.Fragment>
        },
        {
            field: 'matchState', headerName: 'State', width: 250, renderCell: (params: GridCellParams) =>
                <React.Fragment>{getMatchStateText(params.row.matchState)}</React.Fragment>
        },
        {
            field: 'isBadMatch', headerName: 'Bad match', width: 140, renderCell: (params: GridCellParams) =>
                <React.Fragment>{params.row.isBadMatch ? 'Yes' : 'No'}</React.Fragment>
        },
        {
            field: 'score', headerName: 'Score', width: 100, renderCell: (params: GridCellParams) =>
                <React.Fragment>{params.row.score_home} - {params.row.score_away}</React.Fragment>
        },
        {
            field: 'rawScore', headerName: 'Raw', width: 300, renderCell: (params: GridCellParams) =>
                <React.Fragment>{params.row.result_raw}</React.Fragment>
        },
        {
            field: 'bets', headerName: 'Bets', width: 100, renderCell: (params: GridCellParams) =>
                <React.Fragment>{state.matchStats.bets}</React.Fragment>
        },
        {
            field: 'views', headerName: 'Views', width: 100, renderCell: (params: GridCellParams) =>
                <React.Fragment>{state.matchStats.views}</React.Fragment>
        }
    ];

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setState(prevState => ({ ...prevState, tabIndex: newValue }));
    };

    const isRacingSport = (sportId?: number) => sportId === 64 || sportId === 65;
    const isVirtualSport = (sportId?: number) => sportId != null && (VIRTUAL_SPORT_IDS as readonly number[]).includes(sportId);

    const maxTabIndex = React.useMemo(() => {
        return isRacingSport(state.matchInfo?.sport?.id) ? 8 : 7;
    }, [state.matchInfo?.sport?.id]);

    React.useEffect(() => {
        if (state.tabIndex > maxTabIndex) {
            setState(prev => ({ ...prev, tabIndex: 0 }));
        }
    }, [maxTabIndex, state.tabIndex]);

    const onResyncClicked = async () => {
        const userConfirmed = window.confirm('Do you want to re-push events related to this match from the source provider?');
        if (!userConfirmed) return;

        setProviderSyncLoading(true);
        try {
            await resyncSingleMatch(state.matchInfo.syncId, state.matchInfo.providerSource);
            setTimeout(() => {
                fetchData(state.matchId);
                setProviderSyncLoading(false);
            }, 10000); // Continue showing progress for 10 seconds
        } catch (error) {
            setProviderSyncLoading(false);
            setState(prevState => ({ ...prevState, toastMsg: 'Something went wrong' }));
        }
    };

    const onToggleBadMatch = async () => {
        if (!state.loaded) return;
        setBadMatchLoading(true);
        try {
            const nextState = !(state.matchInfo?.isBadMatch ?? false);
            const result = await setMatchBadState(state.matchId, nextState);
            if (result) {
                setState(prevState => ({ ...prevState, matchInfo: { ...prevState.matchInfo, isBadMatch: nextState } }));
            } else {
                setState(prevState => ({ ...prevState, toastMsg: 'Something went wrong' }));
            }
        } catch (error) {
            setState(prevState => ({ ...prevState, toastMsg: 'Something went wrong' }));
        } finally {
            setBadMatchLoading(false);
        }
    };

    const fetchProviderData = async (syncId?: string, source?: string) => {
        if (syncId == null || syncId.length === 0 || source == null || source.length === 0) {
            setState(prevState => ({ ...prevState, providerMatchInfo: null, providerInfoLoading: false }));
            return;
        }

        setState(prevState => ({ ...prevState, providerInfoLoading: true }));
        try {
            const providerMatchInfo = await getMainProviderVarietyInfoDto(syncId, source);
            setState(prevState => ({ ...prevState, providerMatchInfo, providerInfoLoading: false }));
        } catch (error) {
            setState(prevState => ({ ...prevState, providerMatchInfo: null, providerInfoLoading: false }));
        }
    }

    const fetchLiveUrl = async (sportId?: number, syncId?: string) => {
        if (!isVirtualSport(sportId) || !syncId) {
            setLiveUrl(null);
            return;
        }
        setLiveUrlLoading(true);
        try {
            const info = await getVirtualProviderMatchLiveInfo(syncId);
            setLiveUrl(info.liveUrl);
        } catch (error) {
            setLiveUrl(null);
        } finally {
            setLiveUrlLoading(false);
        }
    };

    const fetchData = async (userId: number) => {
        setState(prevState => ({
            ...prevState,
            loaded: false,
            providerMatchInfo: null,
            providerInfoLoading: false
        }));
        setLiveUrl(null);
        let matchInfo = await getFullMatchInfo(userId);
        const sportId = matchInfo?.matchInfo?.sport?.id;
        let racingParticipants: RacingParticipantResultDto[] = [];
        if (isRacingSport(sportId)) {
            racingParticipants = await getRacingResults(userId);
        }
        setState(prevState => ({ ...prevState, matchInfo: matchInfo?.matchInfo ?? { id: matchId } as MatchInfoDto, loaded: matchInfo != null, varieties: matchInfo?.varieties ?? [], matchStats: matchInfo?.stats ?? { views: 0, bets: 0 }, racingParticipants }));
        await fetchProviderData(matchInfo?.matchInfo?.syncId, matchInfo?.matchInfo?.providerSource);
        await fetchLiveUrl(sportId, matchInfo?.matchInfo?.syncId);
    }

    const setToast = React.useCallback((message: string) => {
        setState(prevState => ({ ...prevState, toastMsg: message }));
    }, []);

    const getTabForTabIndex = (index: number) => {
        const participantsTabIndex = isRacingSport(state.matchInfo?.sport?.id) ? 8 : -1;

        switch (index) {
            default: return <MatchVarietiesTab matchId={state.matchId} matchState={state.matchInfo?.matchState} />;
            case 1: return <MatchBetsTab matchId={state.matchId} varieties={state.varieties} />;
            case 2: return <MatchExternalBetsTab matchId={state.matchId} varieties={state.varieties} />;
            case 3: return <MatchBettingResultsTab matchId={state.matchId} onError={setToast} />;
            case 4: return <MatchStatisticsTab matchId={state.matchId} onError={setToast} onSuccess={setToast} />;
            case 5: return <MatchPlayersTab matchId={state.matchId} onError={setToast} onSuccess={setToast} />;
            case 6: return <MatchHistoryTab matchId={state.matchId} onError={setToast} onSuccess={setToast} />;
            case 7: return <MatchPostsTab matchId={state.matchId} sportId={state.matchInfo?.sport?.id} onToast={setToast} />;
            case participantsTabIndex: return <MatchParticipantsTab participants={state.racingParticipants} resultRaw={state.matchInfo?.result_raw} />;
        }
    }

    React.useEffect(() => {
        fetchData(state.matchId);
    }, []);

    return (
        <div>
            <Stack gap="50px 50px" sx={{ mx: 5, my: 5 }} justifyContent='center' alignItems='center'>
                <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 4, lg: 6 }} alignItems='center'>
                    <Grid item xs={0} sm={1} md={2} lg={4} />
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <Button variant="contained" fullWidth={true} style={{ height: '54px' }} onClick={() => navigate('/matches')}>Go to Matches</Button>
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <Button
                            variant="outlined"
                            fullWidth={true}
                            style={{ height: '54px', borderColor: 'green', color: 'green' }}
                            onClick={() => onResyncClicked()}
                            disabled={providerSyncLoading}
                        >
                            {providerSyncLoading ? <CircularProgress size={24} /> : 'Sync with Provider'}
                        </Button>
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <Button
                            variant="outlined"
                            fullWidth={true}
                            style={{ height: '54px', borderColor: state.matchInfo?.isBadMatch ? '#d32f2f' : '#f57c00', color: state.matchInfo?.isBadMatch ? '#d32f2f' : '#f57c00' }}
                            onClick={() => onToggleBadMatch()}
                            disabled={badMatchLoading || !state.loaded}
                        >
                            {badMatchLoading ? <CircularProgress size={24} /> : (state.matchInfo?.isBadMatch ? 'Make active' : 'Mark as bad')}
                        </Button>
                    </Grid>
                    {isVirtualSport(state.matchInfo?.sport?.id) && (
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <Button
                                variant="contained"
                                startIcon={<OpenInNewIcon />}
                                fullWidth={true}
                                style={{ height: '54px' }}
                                disabled={!liveUrl || liveUrlLoading}
                                onClick={() => liveUrl && window.open(liveUrl, '_blank', 'noopener,noreferrer')}
                            >
                                {liveUrlLoading ? <CircularProgress size={24} /> : liveActionLabel(state.matchInfo?.matchState)}
                            </Button>
                        </Grid>
                    )}
                    <Box width="100%" />
                    <Grid item xs={1} sm={1} md={4} lg={6}>
                        <Stack gap="50px 50px" sx={{ mx: 5, my: 5 }} justifyContent='center' alignItems='center' direction='row'>
                            <Avatar
                                alt={state.matchInfo?.home_team?.name}
                                src={state.matchInfo?.home_team?.photo_url}
                                sx={{ width: 72, height: 72 }}
                            />
                            <Typography variant="h5">{state.loaded ? state.matchInfo.home_team?.name + ' vs ' + state.matchInfo.away_team?.name : "Loading match info..."}</Typography>
                            <Avatar
                                alt={state.matchInfo?.away_team?.name}
                                src={state.matchInfo?.away_team?.photo_url}
                                sx={{ width: 72, height: 72 }}
                            />
                        </Stack>
                    </Grid>
                    <Box width="100%" />
                    {!state.loaded ?
                        <React.Fragment>
                            <Grid item xs={0} sm={0} md={1} lg={2} />
                            <Grid item xs={1} sm={2} md={2} lg={2} key="progress">
                                <Stack alignContent='center' alignItems="center" direction="row" sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <CircularProgress size={64} style={{ marginBottom: '32px' }} /></Stack></Grid>

                        </React.Fragment> : <React.Fragment>
                            <Grid item xs={0} sm={0} md={1} lg={2} />
                            <Grid item lg={6} xs={1} sm={2} md={4}>
                                <DataGrid sx={{ width: '100%' }}
                                    rows={[state.matchInfo]}
                                    columns={columns}
                                    initialState={{
                                        pagination: {
                                            paginationModel: { page: 0, pageSize: 25 },
                                        },
                                    }}
                                    pageSizeOptions={[5, 10]}
                                    hideFooterPagination
                                    hideFooter
                                />
                            </Grid>
                            {(state.providerInfoLoading || state.providerMatchInfo != null) && <Box width="100%" />}
                            {(state.providerInfoLoading || state.providerMatchInfo != null) && <Grid item xs={0} sm={0} md={1} lg={2} />}
                            {(state.providerInfoLoading || state.providerMatchInfo != null) && (
                                <Grid item lg={6} xs={1} sm={2} md={4}>
                                    <ProviderMatchEventInfoTables
                                        title="Main Event - Provider information"
                                        info={state.providerMatchInfo}
                                        loading={state.providerInfoLoading}
                                    />
                                </Grid>
                            )}
                            {matchId > 0 && <Box width="100%" />}
                            {matchId > 0 && <Grid item xs={0} sm={0} md={1} lg={2} />}
                            {matchId > 0 && (
                                <Grid item lg={6} xs={1} sm={2} md={4}>
                                    <MatchAiPanel matchId={matchId} onToast={message => setState(prev => ({ ...prev, toastMsg: message }))} />
                                </Grid>
                            )}
                            <Box width="100%" />
                            <Grid item xs={0} sm={0} md={1} lg={2} />
                            <Grid item lg={6} xs={1} sm={2} md={4}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <Tabs value={state.tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                                        <Tab label="Varieties" />
                                        <Tab label="Bets" />
                                        <Tab label="External bets" />
                                        <Tab label="Betting Results" />
                                        <Tab label="Statistics" />
                                        <Tab label="Players" />
                                        <Tab label="History" />
                                        <Tab label="Posts" />
                                        {isRacingSport(state.matchInfo?.sport?.id) && <Tab label="Participants" />}
                                    </Tabs>
                                </Box>
                            </Grid>
                            <Box width="100%" />
                            <Grid item xs={0} sm={0} md={1} lg={2} />
                            <Grid item lg={6} xs={1} sm={2} md={4}>
                                {
                                    getTabForTabIndex(state.tabIndex)
                                }
                            </Grid>
                        </React.Fragment>}
                </Grid>
            </Stack>
            <Snackbar
                open={state.toastMsg != null}
                autoHideDuration={1500}
                message={state.toastMsg}
                onClose={() => { setState(prevState => ({ ...prevState, toastMsg: null })) }}
            />
        </div >
    );
}
